import { useLocation } from "react-router-dom"

import { useFindMyUserOnboardingState } from "~/gen/api/hooks/useFindMyUserOnboardingState"
import {
  resolveSupplierOnboardingStepId,
  type SupplierOnboardingStepId,
} from "~/pages/SupplierPortal/Onboarding/constants/supplierOnboardingSteps"
import { USER_ONBOARDING_STATUS } from "~/pages/SupplierPortal/Onboarding/utils/onboardingEntryDecision"

type OnboardingLocationState = Readonly<{
  initialStepId?: SupplierOnboardingStepId
}>

type OnboardingStatus = "not_started" | "in_progress" | "completed" | "blocked"

type UseUserOnboardingStateResult = Readonly<{
  status: OnboardingStatus
  currentStepId: SupplierOnboardingStepId
  isLoading: boolean
  isError: boolean
  error: Error | null
}>

const resolveOnboardingStatus = (
  apiStatus: string | undefined
): OnboardingStatus => {
  switch (apiStatus) {
    case USER_ONBOARDING_STATUS.IN_PROGRESS:
      return "in_progress"
    case USER_ONBOARDING_STATUS.COMPLETED:
      return "completed"
    case USER_ONBOARDING_STATUS.BLOCKED:
      return "blocked"
    default:
      return "not_started"
  }
}

export const useUserOnboardingState = (): UseUserOnboardingStateResult => {
  const location = useLocation()
  const locationState = location.state as OnboardingLocationState | null

  const { data, isLoading, isError, error } = useFindMyUserOnboardingState()

  const apiState = data?.data.state
  const currentStepId = resolveSupplierOnboardingStepId(
    apiState?.currentStep ?? locationState?.initialStepId
  )

  return {
    status: resolveOnboardingStatus(apiState?.status),
    currentStepId,
    isLoading,
    isError,
    error: error ?? null,
  }
}
